import GuiHandler from "../gui/GuiHandler";
import MapVisConfig from "./MapVisConfig";
import IndicatorHandler from "../gui/IndicatorHandler";
import ChartInteraction from "../vis/ChartInteraction";
import chroma from "chroma-js";
import polylabel from "polylabel";

// Import the entire library
import * as turf from "@turf/turf";
import { chart } from "highcharts";
import DataHandler from "../data/DataHandler";
import * as d3 from "d3";
import mapboxgl, { LngLatLike } from "mapbox-gl";
import {
  HHandPeople,
  IndicatorOverviewObject,
  IndicatorOverviewSingle,
  VisCategory,
} from "../../interfaces/resource.interfaces";
import { Feature, FeatureCollection } from "../../interfaces/map.interfaces";
import StyleHandler from "./StyleHandler";

export default class MapHandler {
  #map: any;
  #token;
  #datahandler: DataHandler;
  #layerConfig: any;
  #currentIndicators: IndicatorOverviewSingle = {};
  #popup!: mapboxgl.Popup;
  #featureCollectionHhPp: Map<string, HHandPeople> = new Map();
  #chartInteraction: ChartInteraction;
  #sparklines: any = [];
  #showSparklines = false;

  #painterCallbackAtMapMove: Function = () => {};

  constructor(container: any, datahandler: DataHandler) {
    this.#token =
      "pk.eyJ1IjoicG1lc2NoZW5tb3NlciIsImEiOiJja3JxNmtrZDkwNG95MnZwcWl2N2trN3VoIn0.SIZHloUAU63Grrn6qbz7BA";
    this.#datahandler = datahandler;
    this.#layerConfig = new MapVisConfig().getLayerConfig();
    this.#chartInteraction = ChartInteraction.getInstance();
    this.#init(container);
    $(container).data("o", this);
    $("#mapghost").data("o", this);

    $("#toggleSparklines").click((event) => {
      this.#showSparklines = !this.#showSparklines;

      if (this.#showSparklines) {
        $("#toggleSparklines").css("border", "3px solid yellow");
        this.addSparklineMarker();
      } else if (!this.#showSparklines) {
        $("#toggleSparklines").css("border", "0px solid yellow");
        this.removeSparklineMarker();
      }
    });
  }

  getMap() {
    return this.#map;
  }

  #init(container: any) {
    const that = this;
    mapboxgl.accessToken = that.#token;
    var style = StyleHandler.getInitialStyle();

    that.#map = new mapboxgl.Map({
      container: container,
      interactive: true,
      center: [8.282136031601055, 47.03698823894112],
      zoom: 13.468330949103825,
      minZoom: 9,
      pitch: 42.162976248608054,
      style: style as any,
      maxBounds: [
        [5.8358140744676303, 45.659168946713827],
        [10.979311848153316, 47.869910020393519],
      ],
    });
    that.#map["doubleClickZoom"].disable();

    $("body").on("keypress", (d) => {
      if (d.key === "l") {
        console.log(
          that.#map.getCenter(),
          that.#map.getZoom()
          //map.getPitch(),
          //map.getBearing()
        );
      }
    });

    that.#map.on("error", (e: any) => {
      // Hide those annoying non-error errors
      if (e && e.error.status !== 400 && e.error.status !== 404)
        console.error(e);
    });

    that.#map.on("move", () => {
      this.#painterCallbackAtMapMove();
    });

    $(".mapboxgl-ctrl-logo").remove();
    $(".mapboxgl-ctrl-attrib-inner").remove();

    that.#map.on("load", (e: any) => {
      new GuiHandler(that.#datahandler, that);
      StyleHandler.setAtmosphere(that.#map);
    });

    that.#popup = new mapboxgl.Popup({
      closeButton: false,
      closeOnClick: true,
    });
    that.#setHoverEffect("indicatorSquares");

    that.#map.on("zoom", () => {
      that.updateMarkerSize();
    });

    that.#map.on("dblclick", (e: any) => {
      var indicatorMeta = $("#indicatorMetaGhost").data("indicatorMeta");
      indicatorMeta = indicatorMeta.find(
        (meta: IndicatorOverviewObject) => meta.category === "Buildings"
      );

      if (
        indicatorMeta?.aggregation === "canton-municipality" &&
        indicatorMeta.category === "Buildings" &&
        $("#indicatorsBuildings select option.customSelectedOption").length > 0
      ) {
        this.#datahandler.requestTranslateLatLonToMunicipality(
          {
            p_lon: e.lngLat.lng,
            p_lat: e.lngLat.lat,
          },
          (data: any) => {
            var indicatorMetaCopy = indicatorMeta;
            var settingsGhost = $("#indicatorsettingsghost").data("settings");
            var paramsGhost = settingsGhost.indicator.parameters;
            paramsGhost.p_municipality = data[0].bfsid;
            var settings = {
              indicator: {
                name: settingsGhost.indicator.name,
                parameters: paramsGhost,
                viscategory: settingsGhost.indicator.viscategory,
                minyear: settingsGhost.indicator.minyear,
              },
            };
            //if p_canton in settings is set, remove it
            if (settings.indicator.parameters.p_canton) {
              delete settings.indicator.parameters.p_canton;
            }
            this.#datahandler.requestIndicator(settings, (data: any) => {
              that.visualizeIndicator(data, indicatorMetaCopy);
            });
          }
        );
      }
    });
  }

  visualizeIndicator(
    featureCollection: FeatureCollection,
    indicatorMeta: IndicatorOverviewObject
  ) {
    const that = this;
    const visCategory = indicatorMeta.viscategory;
    const config = that.#layerConfig[visCategory];
    const sourceAndLayerName = `indicator${visCategory}`;
    that.#currentIndicators[visCategory] = indicatorMeta;

    if (visCategory === "Squares") {
      if (that.#sparklines) {
        that.#sparklines.forEach((sparkline: any) => sparkline.remove());
        that.#sparklines = [];
      }
      if (featureCollection.features != undefined) {
        featureCollection.features.forEach((feature) => {
          const centroid: any = this.getBestCentroid(feature);

          if (feature.properties.sparklineSVG) {
            const el = document.createElement("div");
            el.className = "sparkline-container";
            el.appendChild(feature.properties.sparklineSVG);
            el.style.display = "none";

            const marker = new mapboxgl.Marker(el, {
              rotationAlignment: "map",
            })
              .setLngLat(centroid.centroidPoint.geometry.coordinates as any)
              .addTo(that.#map);
            this.#sparklines.push(marker);
          }
          if (feature.properties.sparklineDetailedSVG != undefined) {
            const el = document.createElement("div");
            el.className = "sparkline-container-detailed";
            el.appendChild(feature.properties.sparklineDetailedSVG);
            el.style.display = "none";

            const marker = new mapboxgl.Marker(el, {
              rotationAlignment: "map",
            })
              .setLngLat(
                centroid?.centroidPoint.geometry.coordinates as [number, number]
              )
              .addTo(that.#map);
            this.#sparklines.push(marker);
          }
        });
        this.updateMarkerSize();
      }
    }
    if (that.#popup) that.#popup.remove();
    if (featureCollection && featureCollection.features) {
      var colorscaledomain = indicatorMeta.colorscaledomain;
      if (!colorscaledomain)
        colorscaledomain = d3.extent(
          featureCollection.features,
          (d) => d.properties.indicator
        );
      var colorScale: any;
      var colorScaleMiddle: any;
      var colorScaleMin: any;
      var colorScaleMax: any;
      if (
        indicatorMeta.colorscheme.type === "d3interpolate" &&
        indicatorMeta.colorscheme.value === "interpolateBlues"
      ) {
        // changed color of squares
        //let color = config.interaction.getSquaresColor();
        let color = "#08306b";

        if (color !== "#08306b") {
          colorScale = d3
            .scaleSequential()
            .domain(colorscaledomain)
            .interpolator(d3.interpolate("white", color));
        } else {
          colorScale = d3
            .scaleSequential()
            .domain(colorscaledomain)
            // @ts-ignore
            .interpolator(d3[indicatorMeta.colorscheme.value]);
        }
        var values: number[] = [];
        featureCollection.features.forEach((f) => {
          values.push(f.properties.indicator);
          f.properties.color = colorScale(f.properties.indicator);
        });
        if (that.#chartInteraction.getCustomScaleBool()) {
          var customScaleMax = that.#chartInteraction.getCustomScaleMax();
          var customScaleMin = that.#chartInteraction.getCustomScaleMin();
          var customMiddleColor = that.#chartInteraction.getCustomMiddleColor();
          var customMinColor = that.#chartInteraction.getCustomMinColor();
          var customMaxColor = that.#chartInteraction.getCustomMaxColor();
          var interpolateBool = that.#chartInteraction.getInterpolateBool();
          var customMiddleColorLight = chroma(customMiddleColor)
            .brighten(2)
            .hex();
          if (interpolateBool === true) {
            // change color of features in featureCollection which are below min to red and above max to green when a custom scale is used
            featureCollection.features.forEach((feature) => {
              var ind = feature.properties.indicator;
              colorScaleMiddle = d3
                .scaleSequential()
                .domain([customScaleMin, customScaleMax])
                .interpolator(
                  d3.interpolate(customMiddleColorLight, customMiddleColor)
                );
              if (ind <= customScaleMin) {
                feature.properties.color = customMinColor;
              } else if (ind >= customScaleMax) {
                feature.properties.color = customMaxColor;
              } else {
                feature.properties.color = colorScaleMiddle(
                  feature.properties.indicator
                );
              }
            });
          } else {
            colorScaleMin = d3
              .scaleSequential()
              .domain([colorscaledomain[0], customScaleMin])
              .interpolator(d3.interpolate("white", customMinColor));
            colorScaleMiddle = d3
              .scaleSequential()
              .domain([customScaleMin, customScaleMax])
              .interpolator(d3.interpolate(customMinColor, customMiddleColor));
            colorScaleMax = d3
              .scaleSequential()
              .domain([customScaleMax, colorscaledomain[1]])
              .interpolator(d3.interpolate(customMiddleColor, customMaxColor));
            featureCollection.features.forEach((feature) => {
              var ind = feature.properties.indicator;
              if (ind <= customScaleMin) {
                feature.properties.color = colorScaleMin(
                  feature.properties.indicator
                );
              } else if (ind >= customScaleMax) {
                feature.properties.color = colorScaleMax(
                  feature.properties.indicator
                );
              } else {
                feature.properties.color = colorScaleMiddle(
                  feature.properties.indicator
                );
              }
            });
          }
        }

        if (
          featureCollection?.meta?.type === "difference" &&
          !that.#chartInteraction.getCustomScaleBool()
        ) {
          // Diverging color scale from red to blue
          const divergingExtent = d3.extent(
            featureCollection.features,
            (d) => d.properties.indicator
          );
          // get the larger absolute value of the extent
          const absExtent = Math.max(
            Math.abs(divergingExtent[0]),
            Math.abs(divergingExtent[1])
          );
          colorScale = d3
            .scaleDiverging(d3.interpolateRdBu)
            .domain([-1 * absExtent, 0, absExtent]);

          var canvas = document.createElement("canvas");
          canvas.width = 256;
          canvas.height = 1;
          var context = canvas.getContext("2d");
          var gradient = context?.createLinearGradient(0, 0, canvas.width, 0);

          var domain = colorScale.domain();
          var numStops = 11;
          var domainRange = domain[2] - domain[0];

          for (var i = 0; i <= numStops; i++) {
            var value = domain[0] + (i / numStops) * domainRange;
            var col = colorScale(value);
            gradient?.addColorStop(i / numStops, col);
          }
          if (context && gradient) {
            context.fillStyle = gradient;
            context.fillRect(0, 0, canvas.width, canvas.height);
          }

          var colorScheme = canvas.toDataURL();

          featureCollection.features.forEach((f) => {
            f.properties.color = colorScale(f.properties.indicator);
          });

          config.legend.setDivergingColorScale(colorScheme, colorScale);
        }
        config.legendChart.setData(values, false);

        // set the values for custom scaling that it matches the selected filter
        var max = Math.max.apply(Math, values);
        var oneThird = max / 3;
        var twoThird = oneThird * 2;
        oneThird = +oneThird.toFixed(2);
        twoThird = +twoThird.toFixed(2);
        that.#chartInteraction.setCustomScaleMax(twoThird);
        that.#chartInteraction.setCustomScaleMin(oneThird);

        if (
          featureCollection?.meta?.type != "difference" &&
          !that.#chartInteraction.getCustomScaleBool()
        ) {
          config.legend.setSequentialColorScale(config.colorScheme, colorScale);
        }
      } else if (
        indicatorMeta.colorscheme.type === "d3interpolate" &&
        indicatorMeta.colorscheme.value === "interpolateReds"
      ) {
        colorScale = d3
          .scaleSequential()
          .domain(colorscaledomain)
          // @ts-ignore
          .interpolator(d3[indicatorMeta.colorscheme.value]);
        var values: number[] = [];
        featureCollection.features.forEach((f) => {
          values.push(f.properties.indicator);
          f.properties.color = colorScale(f.properties.indicator);
        });
        config.legendChart.setData(values, false);
        config.legend.setSequentialColorScale(
          config.colorScheme,
          colorScale,
          false
        );
      } else if (
        ["d3categorical", "customArray"].indexOf(
          indicatorMeta.colorscheme.type
        ) > -1
      ) {
        var counter: any = {};
        indicatorMeta.ordinaldomain.forEach(
          (category) => (counter[category] = 0)
        );
        var colorRange;
        if (indicatorMeta.colorscheme.type === "d3categorical") {
          // @ts-ignore
          colorRange = d3[indicatorMeta.colorscheme.value].filter(
            (item: string) => item !== "#1f77b4"
          );
        } else if (indicatorMeta.colorscheme.type === "customArray") {
          colorRange = indicatorMeta.colorscheme.value;
        }
        colorScale = d3
          .scaleOrdinal(colorRange)
          .domain(indicatorMeta.ordinaldomain);
        featureCollection.features.forEach((f) => {
          counter[f.properties.indicator]++;
          f.properties.color = colorScale(f.properties.indicator);
        });
        var counterValues: { label: string; value: number }[] = [];
        indicatorMeta.ordinaldomain.forEach((category) =>
          counterValues.push({ label: category, value: counter[category] })
        );
        config.legendChart.setData(counterValues, true);
        config.legend.setSelectionCallback(
          (value: any, isDeselect: boolean) => {
            const layerName = `indicator${indicatorMeta.viscategory}`;
            that.updateFeatureProperty(
              layerName,
              "selected",
              !isDeselect,
              "indicator",
              value
            );
          }
        );
        config.legend.setOrdinalColorScale(colorScale, indicatorMeta);
      }
      $(`#legendLabel${visCategory}`)
        .text(indicatorMeta.displayname)
        .css("opacity", 1);
      var src = that.#map.getSource(sourceAndLayerName);
      if (!src) {
        that.#map.addSource(sourceAndLayerName, {
          type: "geojson",
          data: featureCollection,
        });
      } else {
        src.setData(featureCollection);
      }
      if (that.#map.getLayer(sourceAndLayerName)) {
        that.#map.removeLayer(sourceAndLayerName);
      }
      if (
        indicatorMeta?.aggregation !== "municipality-square" &&
        visCategory === "Squares"
      ) {
        var layerTemplate = config.layerTemplateAggregate;
      } else {
        var layerTemplate = config.layerTemplate;
      }
      layerTemplate.id = sourceAndLayerName;
      layerTemplate.source = sourceAndLayerName;
      that.#map.addLayer(layerTemplate);

      that.setLayerOrder(new MapVisConfig().getLayerOrder());
    } else {
      if (that.#map.getLayer(sourceAndLayerName))
        that.#map.removeLayer(sourceAndLayerName);
      config.legendChart.clear();
      config.legend.hide();
    }
  }

  visualizeBoundaries(geojson: any, name: string) {
    if (geojson) {
      const that = this;
      const srcAndLayerName = `${name}-boundaries`;
      const config = that.#layerConfig[srcAndLayerName];
      var src = this.#map.getSource(srcAndLayerName);
      if (!src) {
        this.#map.addSource(srcAndLayerName, {
          type: "geojson",
          data: geojson,
        });
      } else {
        src.setData(geojson);
      }
      if (!this.#map.getLayer(srcAndLayerName))
        this.#map.addLayer(config.layerTemplate);
    }
  }

  removeIndicator(isBuildingIndicator: boolean = false) {
    const that = this;
    var visCategory: VisCategory = isBuildingIndicator
      ? "Buildings"
      : "Squares";
    const sourceAndLayerName = `indicator${visCategory}`;
    if (that.#map.getLayer(sourceAndLayerName))
      that.#map.removeLayer(sourceAndLayerName);
    if (that.#map.getSource(sourceAndLayerName))
      that.#map.removeSource(sourceAndLayerName);
    this.#layerConfig[visCategory].legend.hide();
    this.#layerConfig[visCategory].legendChart.clear();
    if (visCategory === "Squares")
      this.#sparklines.forEach((sparkline: any) => sparkline.remove());
    $(`#legendLabel${visCategory}`).css("opacity", 0);
  }

  setLayerOrder(orderedLayerNames: string[]) {
    const that = this;
    orderedLayerNames.forEach((layerName) => {
      if (that.#map.getLayer(layerName)) that.#map.moveLayer(layerName);
    });
  }

  getBestCentroid(feature: any) {
    const geometry = feature.geometry;
    let centroid, centroidPoint, distance;

    if (geometry.type === "Polygon") {
      centroid = polylabel(geometry.coordinates, 0.0001);
      centroidPoint = turf.point([centroid[0], centroid[1]]);
      distance = centroid.distance;
      return { centroidPoint, distance };
    } else if (geometry.type === "MultiPolygon") {
      let largestPolygon: any = null;
      geometry.coordinates.forEach((polygon: any) => {
        const area = turf.area(turf.polygon(polygon));
        if (!largestPolygon || area > largestPolygon.area) {
          largestPolygon = { area: area, polygon: polygon };
        }
      });

      centroid = polylabel(largestPolygon.polygon, 0.0001);
      centroidPoint = turf.point([centroid[0], centroid[1]]);
      distance = centroid.distance;
      return { centroidPoint, distance };
    }
  }

  updateFeatureProperty(
    sourceName: string,
    propertyName: string,
    propertyValue: boolean,
    relevantKey: string,
    relevantValue: string
  ) {
    const src = this.#map.getSource(sourceName);
    if (src) {
      const featureCollection = src._data;
      const withFilter = (feature: Feature) => {
        if (feature.properties[relevantKey] === relevantValue)
          feature.properties[propertyName] = propertyValue;
      };
      const withoutFilter = (feature: Feature) => {
        feature.properties[propertyName] = propertyValue;
      };
      featureCollection.features.forEach(
        relevantKey ? withFilter : withoutFilter
      );
      src.setData(featureCollection);
    }
  }

  #setHoverEffect(layerName: string) {
    const that = this;
    that.#map.on("mousemove", layerName, function (e: any) {
      var mapZoom = that.#map.getZoom();
      if (mapZoom > 13) {
        that.#map.getCanvas().style.cursor = "pointer";
      }
    });

    //MapBox mouse move event exit
    that.#map.on("mouseleave", layerName, function () {
      that.#map.getCanvas().style.cursor = "";
      that.#map.getCanvas().style.cursor = "grab";
      if (that.#popup) {
        that.#popup.remove();
      }
    });
    console.log(layerName);
    //MapBox mouse click event
    that.#map.on("click", layerName, function (e: any) {
      const indicatorMeta =
        that.#currentIndicators[layerName.replace("indicator", "")];
      var mapZoom = that.#map.getZoom();
      var indicatorName = indicatorMeta?.name;
      if (mapZoom > 10) {
        that.#map.getCanvas().style.cursor = "pointer";

        var center = turf.pointOnFeature(
          turf.polygon(e.features[0].geometry.coordinates.slice())
        );
        var indicator = e.features[0].properties.indicator;
        var HhPp = that.getFeatureHhPpByCoords(
          e.features[0].properties.id.toString()
        );
        var name = e.features[0].properties.name;
        name = name === "" ? "Hektare " + e.features[0].properties.id : name;

        //differ between abs and rel values
        var element = $(`.${indicatorMeta?.name}`);
        var abs_rel = element.find("fieldset").find("input:checked").val();
        if (abs_rel === undefined) {
          abs_rel = indicatorName?.substr(indicatorName.length - 3);
        }
        if (indicatorName === "living_family_area_h_abs") {
          indicator = indicator.toString();
        } else if (abs_rel === "rel" || abs_rel === "n_h") {
          indicator = indicator * 100;
          indicator = indicator.toString().substring(0, 4);
          indicator = indicator + "%";
        } else if (abs_rel === "abs") {
          indicator = Math.round(indicator * 100) / 100;
          indicator = indicator.toString();
        } else {
          indicator = indicator.toString();
        }
        if (Object.keys(HhPp).length === 0) {
          that.#popup
            .setLngLat(center.geometry.coordinates as [number, number])
            .setHTML(
              `<div class="popup" align="center"><div class="popup-title" style="margin-left:15px;"><strong>${name}:</strong></div><div class="popup-indicator-value" align="right">"${indicatorMeta?.displayname}" Indikator: ${indicator}</div></div></div>`
            )
            .addClassName("popup-maxwidth-300")
            // @ts-ignore
            .addTo(that.#map);
        } else {
          that.#popup
            .setLngLat(center.geometry.coordinates as [number, number])
            .setHTML(
              `<div class="popup" align="center"><div class="popup-title" style="margin-left:15px;"><strong>${name}:</strong></div><div class="popup-indicator-value" align="right">"${indicatorMeta?.displayname}" Indikator: ${indicator}</div><div class="popup-indicator-person" style="text-align: right;">Anz. Personen: ${HhPp.pp}</div><div class="popup-indicator-person" style="text-align: right;">Anz. Haushalte: ${HhPp.hh}</div></div></div>`
            )
            .addClassName("popup-maxwidth-300")
            // @ts-ignore
            .addTo(that.#map);
        }
      } else {
        that.#popup.remove();
      }
    });
  }

  zoomToBoundingbox(bbox: [number, number][]) {
    this.#map.fitBounds(bbox, { duration: 0 });
  }

  setPainterCallbackAtMapMove(cb: Function) {
    this.#painterCallbackAtMapMove = cb;
  }

  setFeatureCollectionHHAndPeople(featureCollectionHhPp: HHandPeople[]) {
    this.#featureCollectionHhPp = new Map(
      featureCollectionHhPp.map((feature) => [feature.id, feature])
    );
  }

  getFeatureHhPpByCoords(id: string) {
    const feature = this.#featureCollectionHhPp.get(id);
    return feature ? { pp: feature.indicatorppl, hh: feature.indicatorhh } : {};
  }

  getScale(meter: number) {
    var center = this.#map.getCenter();
    var zoom = this.#map.getZoom();
    var tmp =
      (156543.03392 * Math.cos((center.lat * Math.PI) / 180)) /
      Math.pow(2, zoom);
    var meterSizeInPixelNM = meter / tmp;

    return [meterSizeInPixelNM, meterSizeInPixelNM];
  }

  toggleSparklines() {
    this.#showSparklines = !this.#showSparklines;
    this.updateMarkerSize();
  }

  showSparklines() {
    return this.#showSparklines;
  }

  addSparklineMarker() {
    var indicatorHandler = $("#indicatorghost").data("obj");
    var meta = $("#indicatorMetaGhost").data("indicatorMeta")[0];
    var settings = $("#indicatorsettingsghost").data("settings");

    var validIndicatorSelection =
      $("#indicatorWrapper .customSelectedOption").closest(
        "#indicatorsPeople, #indicatorsLiving"
      ).length > 0;

    if (validIndicatorSelection) {
      indicatorHandler.fetchSparklines(settings, meta).then((data: any) => {
        const featureCollection: FeatureCollection =
          this.#map.getSource("indicatorSquares")._data;

        featureCollection.features.forEach((feature) => {
          var id = feature.properties.id;

          var matchingData = data[id];
          if (matchingData) {
            feature.properties.sparklineSVG = matchingData.sparklineSVG;
            feature.properties.sparklineDetailedSVG =
              matchingData.sparklineDetailedSVG;
          }
        });

        this.#map.getSource("indicatorSquares").setData(featureCollection);
        featureCollection.features.forEach((feature) => {
          const centroid = this.getBestCentroid(feature);

          if (feature.properties.sparklineSVG) {
            const el = document.createElement("div");
            el.className = "sparkline-container";
            el.appendChild(feature.properties.sparklineSVG);
            el.style.display = "none";

            const marker = new mapboxgl.Marker(el, {
              rotationAlignment: "map",
            })
              .setLngLat(
                centroid?.centroidPoint.geometry.coordinates as [number, number]
              )
              .addTo(this.#map);
            this.#sparklines.push(marker);
          }
          if (feature.properties.sparklineDetailedSVG != undefined) {
            const el = document.createElement("div");
            el.className = "sparkline-container-detailed";
            el.appendChild(feature.properties.sparklineDetailedSVG);
            el.style.display = "none";

            const marker = new mapboxgl.Marker(el, {
              rotationAlignment: "map",
            })
              .setLngLat(
                centroid?.centroidPoint.geometry.coordinates as [number, number]
              )
              .addTo(this.#map);
            this.#sparklines.push(marker);
          }
        });
        this.updateMarkerSize();
      });
    }
  }
  removeSparklineMarker() {
    this.#sparklines
      .filter((sparkline: any) => {
        const element = sparkline.getElement();
        return (
          element.classList.contains("sparkline-container") ||
          element.classList.contains("sparkline-container-detailed")
        );
      })
      .forEach((sparkline: any) => sparkline.remove());

    if (this.#map.getSource("indicatorSquares")) {
      var featureCollection: FeatureCollection =
        this.#map.getSource("indicatorSquares")._data;

      featureCollection.features.forEach((feature) => {
        delete feature.properties.sparklineSVG;
        delete feature.properties.sparklineDetailedSVG;
      });

      this.#map.getSource("indicatorSquares").setData(featureCollection);
    }
  }

  updateMarkerSize() {
    var aggregation = this.#currentIndicators.Squares?.aggregation;
    var zoomBounds: [number, number] = [0, 0];
    var meter = 100;
    var zoom = this.#map.getZoom();

    if (aggregation != undefined) {
      if (aggregation == "municipality-square") {
        zoomBounds = [15, 16.8];
        meter = 100;
      } else if (aggregation == "municipality-quarter") {
        zoomBounds = [13, 15];
        meter = 200;
      } else if (
        aggregation == "canton-municipality" ||
        aggregation == "canton-district"
      ) {
        zoomBounds = [10, 12.3];
        meter = 2000;
      }

      var sparklines = this.#sparklines.filter((sparkline: any) => {
        return sparkline.getElement().classList.contains("sparkline-container");
      });

      var sparklinesDetailed = this.#sparklines.filter((sparkline: any) => {
        return sparkline
          .getElement()
          .classList.contains("sparkline-container-detailed");
      });
      if (zoom < zoomBounds[0] || !this.#showSparklines) {
        sparklines.forEach((marker: any) => {
          marker.getElement().style.display = "none";
        });
        sparklinesDetailed.forEach((marker: any) => {
          marker.getElement().style.display = "none";
        });
      } else if (zoom < zoomBounds[1] && zoom >= zoomBounds[0]) {
        sparklines.forEach((marker: any) => {
          marker.getElement().style.display = "";
          var pixelSizes = this.getScale(meter);
          marker.getElement().style.width = pixelSizes[0] + "px";
          marker.getElement().style.height = pixelSizes[1] + "px";
        });
        sparklinesDetailed.forEach((marker: any) => {
          marker.getElement().style.display = "none";
        });
      } else if (zoom >= zoomBounds[1]) {
        sparklines.forEach((marker: any) => {
          marker.getElement().style.display = "none";
        });
        sparklinesDetailed.forEach((marker: any) => {
          marker.getElement().style.display = "";
          var pixelSizes = this.getScale(meter);
          marker.getElement().style.width = pixelSizes[0] + "px";
          marker.getElement().style.height = pixelSizes[1] + "px";
        });
      }
    }
  }
}
